<script setup lang="ts">
/**
 * ContextMenu — Right-click context menu for modules
 * Shows state changes, charge selection, info, remove options
 */
import { ref, onMounted, onUnmounted } from 'vue';
import type { ModuleState } from '../../../shared/types';

export interface ContextMenuItem {
  label: string;
  icon?: string;
  action: string;
  disabled?: boolean;
  divider?: boolean;
  active?: boolean;
}

const props = defineProps<{
  x: number;
  y: number;
  items: ContextMenuItem[];
}>();

const emit = defineEmits<{
  'select': [action: string];
  'close': [];
}>();

const menuRef = ref<HTMLElement | null>(null);
const adjustedX = ref(props.x);
const adjustedY = ref(props.y);

function handleClickOutside(e: MouseEvent): void {
  if (menuRef.value && !menuRef.value.contains(e.target as Node)) {
    emit('close');
  }
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close');
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  document.addEventListener('keydown', handleKeydown);

  // Adjust position to fit within viewport
  requestAnimationFrame(() => {
    if (!menuRef.value) return;
    const rect = menuRef.value.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (rect.right > vw) adjustedX.value = vw - rect.width - 4;
    if (rect.bottom > vh) adjustedY.value = vh - rect.height - 4;
    if (adjustedX.value < 0) adjustedX.value = 4;
    if (adjustedY.value < 0) adjustedY.value = 4;
  });
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      ref="menuRef"
      class="context-menu"
      :style="{ left: adjustedX + 'px', top: adjustedY + 'px' }"
    >
      <template v-for="(item, i) in items" :key="i">
        <div v-if="item.divider" class="context-divider" />
        <button
          v-else
          class="context-item"
          :class="{ 'context-item--disabled': item.disabled, 'context-item--active': item.active }"
          :disabled="item.disabled"
          @click="emit('select', item.action); emit('close')"
        >
          <span v-if="item.icon" class="context-icon">{{ item.icon }}</span>
          <span class="context-label">{{ item.label }}</span>
        </button>
      </template>
    </div>
  </Teleport>
</template>

<style scoped>
.context-menu {
  position: fixed;
  z-index: 2000;
  min-width: 160px;
  max-width: 260px;
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: 4px 0;
  animation: ctx-in 0.1s ease-out;
}

@keyframes ctx-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.context-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 12px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.78rem;
  cursor: pointer;
  text-align: left;
  transition: background var(--transition-fast);
}

.context-item:hover:not(:disabled) {
  background: var(--bg-hover);
}

.context-item--disabled {
  color: var(--text-secondary);
  opacity: 0.5;
  cursor: default;
}

.context-item--active {
  color: var(--accent-blue);
}

.context-icon {
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.context-label {
  flex: 1;
}

.context-divider {
  height: 1px;
  background: var(--border-color);
  margin: 3px 8px;
}
</style>
